import * as THREE from 'three/webgpu';
import type { FeedbackEvent } from '../sim/feedback';
import type { MotionState } from '../sim/motion';
import type { V3 } from '../sim/vec';

/**
 * Pooled dust/thruster particles — a feedback-bus subscriber. One THREE.Points draw call for the
 * whole pool (R1 rule). Randomness lives here, never in the sim.
 */
const POOL = 160;

export class DustVfx {
  readonly points: THREE.Points;
  private readonly positions: Float32Array;
  private readonly velocities: Float32Array;
  private readonly life: Float32Array;
  private cursor = 0;

  constructor() {
    this.positions = new Float32Array(POOL * 3).fill(-999);
    this.velocities = new Float32Array(POOL * 3);
    this.life = new Float32Array(POOL);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    const mat = new THREE.PointsMaterial({
      color: 0xcfc9b8,
      size: 0.09,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
    });
    this.points = new THREE.Points(geo, mat);
    this.points.frustumCulled = false;
  }

  handle(events: FeedbackEvent[], feet: V3): void {
    for (const e of events) {
      if (e.type === 'land') {
        const count = Math.round(6 + 16 * e.intensity);
        for (let i = 0; i < count; i++) {
          const a = (i / count) * Math.PI * 2 + Math.random() * 0.4;
          this.spawn(
            { x: feet.x + Math.cos(a) * 0.25, y: feet.y + 0.05, z: feet.z + Math.sin(a) * 0.25 },
            {
              x: Math.cos(a) * (1 + 2.5 * e.intensity),
              y: 0.6 + Math.random() * 0.8,
              z: Math.sin(a) * (1 + 2.5 * e.intensity),
            },
            0.45 + Math.random() * 0.2,
          );
        }
      } else if (e.type === 'jump' || e.type === 'skid' || e.type === 'roll') {
        for (let i = 0; i < 5; i++) {
          this.spawn(
            { x: feet.x, y: feet.y + 0.05, z: feet.z },
            { x: (Math.random() - 0.5) * 2, y: 0.4 + Math.random(), z: (Math.random() - 0.5) * 2 },
            0.35,
          );
        }
      }
    }
  }

  /**
   * Positioned burst for ENTITIES (E0): the feedback bus carries no position, so entity breaks
   * reach the pool through this hook (mount wires it into EntityCtx via scene.burstAt).
   */
  burst(pos: V3, count: number): void {
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + Math.random() * 0.5;
      this.spawn(
        { x: pos.x + Math.cos(a) * 0.1, y: pos.y + Math.random() * 0.2, z: pos.z + Math.sin(a) * 0.1 },
        { x: Math.cos(a) * (1 + Math.random()), y: 1 + Math.random() * 1.5, z: Math.sin(a) * (1 + Math.random()) },
        0.4 + Math.random() * 0.25,
      );
    }
  }

  /** Per-frame: thruster particles while hovering, then integrate the pool. */
  update(state: MotionState, feet: V3, dt: number): void {
    if (state.mode === 'hover' && Math.random() < 0.7) {
      this.spawn(
        {
          x: feet.x + (Math.random() - 0.5) * 0.3,
          y: feet.y + 0.05,
          z: feet.z + (Math.random() - 0.5) * 0.3,
        },
        { x: (Math.random() - 0.5) * 0.6, y: -2.2 - Math.random(), z: (Math.random() - 0.5) * 0.6 },
        0.3,
      );
    }

    for (let i = 0; i < POOL; i++) {
      if (this.life[i]! <= 0) continue;
      this.life[i]! -= dt;
      const j = i * 3;
      if (this.life[i]! <= 0) {
        this.positions[j + 1] = -999;
        continue;
      }
      this.velocities[j + 1]! -= 4 * dt; // light gravity on dust
      this.positions[j]! += this.velocities[j]! * dt;
      this.positions[j + 1]! += this.velocities[j + 1]! * dt;
      this.positions[j + 2]! += this.velocities[j + 2]! * dt;
    }
    (this.points.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
  }

  private spawn(pos: V3, vel: V3, life: number): void {
    const i = this.cursor;
    this.cursor = (this.cursor + 1) % POOL;
    const j = i * 3;
    this.positions[j] = pos.x;
    this.positions[j + 1] = pos.y;
    this.positions[j + 2] = pos.z;
    this.velocities[j] = vel.x;
    this.velocities[j + 1] = vel.y;
    this.velocities[j + 2] = vel.z;
    this.life[i] = life;
  }
}
